import React, { useCallback, useContext, useState } from 'react';
import styled from 'styled-components';

import Row from 'components/Common/Row';
import Alert from 'kingpin/Alert';
import Typography from 'kingpin/atoms/Typography';
import FormInput from 'kingpin/forms/FormInput';

import { AdditionalPaymentsModeContext } from './AdditionalPaymentsModeProvider';
import AdditionalPaymentsSummary from './AdditionalPaymentsSummary';
import CustomAmountInput from './CustomAmountInput';
import InlineFieldError from './InlineFieldError';
import NoteInput from './NoteInput';
import useFormatPaymentAmount from './useFormatPaymentAmount';

const AmountTile = styled.button<{ $isSelected: boolean }>`
  min-width: 96px;
  padding: 12px 16px;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid
    ${(props) =>
      props.$isSelected
        ? 'var(--color-info-v2)'
        : 'var(--color-border-primary)'};
  background-color: ${(props) =>
    props.$isSelected ? 'var(--color-bg-selected)' : 'var(--color-bg-primary)'};
  box-shadow: ${(props) =>
    props.$isSelected ? '0px 0px 0px 1px var(--color-info-v2)' : 'none'};
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  &:hover {
    border-color: var(--color-info-v2);
  }
`;

// The add view's body: budget, amount and note. The Cancel / Add actions live
// in the mode header (AdditionalPaymentsModeActions).
const AddAdditionalPaymentForm = ({
  bonusOverview,
  response,
}: {
  bonusOverview: BonusTypes.BonusOverview;
  response: BonusTypes.AdditionalPayments.Response;
}) => {
  const {
    availablePresets,
    selectedPreset,
    customAmount,
    note,
    isNoteRequired,
    selectPreset,
    setCustomAmount,
    setNote,
    amountError,
    validAmount,
    maxAddable,
    serverError,
  } = useContext(AdditionalPaymentsModeContext);
  const formatAmount = useFormatPaymentAmount();
  // With presets configured, the custom amount stays hidden until "Other" is
  // picked. Without any, it's the only way to enter an amount.
  const hasPresets = availablePresets.length > 0;
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const isCustomAmountShown = !hasPresets || isOtherSelected;

  const onPresetClicked = useCallback(
    (preset: BonusOverrideSettings.AdditionalPaymentPreset) => {
      setIsOtherSelected(false);
      selectPreset(preset);
    },
    [selectPreset],
  );

  const onOtherClicked = useCallback(() => {
    setIsOtherSelected(true);
    // Clears any chosen preset too.
    setCustomAmount(undefined);
  }, [setCustomAmount]);

  return (
    <>
      <AdditionalPaymentsSummary
        bonusOverview={bonusOverview}
        response={response}
        pendingAmount={validAmount}
      />
      {maxAddable > 0 ? (
        <>
          <FormInput label={'Amount'}>
            <>
              {hasPresets && (
                <Row style={{ gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                  {availablePresets.map((preset) => (
                    <AmountTile
                      key={preset.amount}
                      type={'button'}
                      $isSelected={selectedPreset?.amount === preset.amount}
                      onClick={() => onPresetClicked(preset)}
                      data-testid={`additional-payment-preset-${preset.amount}`}
                    >
                      <Typography.Header type={'H4'}>
                        {formatAmount(preset.amount)}
                      </Typography.Header>
                      {preset.label && (
                        <Typography.Body
                          type={'Body 12'}
                          color={'var(--color-text-muted)'}
                        >
                          {preset.label}
                        </Typography.Body>
                      )}
                    </AmountTile>
                  ))}
                  <AmountTile
                    type={'button'}
                    $isSelected={isOtherSelected}
                    onClick={onOtherClicked}
                    data-testid={'additional-payment-preset-other'}
                  >
                    <Typography.Header type={'H4'}>{'Other'}</Typography.Header>
                  </AmountTile>
                </Row>
              )}
              {isCustomAmountShown && (
                <>
                  <CustomAmountInput
                    value={customAmount}
                    setValue={setCustomAmount}
                    placeholder={
                      hasPresets ? 'Other amount' : 'Amount, e.g. 50'
                    }
                    hasError={customAmount !== undefined && !!amountError}
                    isAutoFocused={hasPresets}
                    testId={'additional-payment-custom-amount'}
                  />
                  {amountError && <InlineFieldError message={amountError} />}
                </>
              )}
            </>
          </FormInput>
          <NoteInput
            value={note}
            setValue={setNote}
            isRequired={isNoteRequired}
            testId={'additional-payment-note'}
          />
        </>
      ) : (
        <Alert
          type={'Info'}
          body={
            response.policy
              ? "This driver has reached this period's additional payment limit."
              : "Additional payments aren't enabled for this period."
          }
        />
      )}
      {serverError && (
        <div style={{ marginTop: 16 }}>
          <Alert type={'Danger'} body={serverError} />
        </div>
      )}
    </>
  );
};

export default AddAdditionalPaymentForm;
